import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Cookie, ShieldCheck, X, Check } from "@phosphor-icons/react";
import {
  readConsent, writeConsent, isGPC, OPEN_PREFS_EVENT,
} from "../lib/cookieConsent";

/* Always-on toggle row for the preferences dialog */
function CategoryRow({ label, desc, enabled, locked, onChange, testid }) {
  return (
    <div className="flex items-start justify-between gap-4 py-3 border-b border-ink-100 last:border-b-0" data-testid={testid}>
      <div className="flex-1">
        <div className="font-display font-semibold text-base">{label}</div>
        <p className="text-sm text-ink-700 mt-0.5">{desc}</p>
      </div>
      <button
        type="button"
        disabled={locked}
        onClick={() => !locked && onChange(!enabled)}
        className={`relative inline-flex h-7 w-12 flex-shrink-0 rounded-full transition-colors ${
          locked ? "bg-ink-300" : enabled ? "bg-cobalt-500" : "bg-ink-200"
        } ${locked ? "opacity-70 cursor-not-allowed" : "cursor-pointer"}`}
        aria-pressed={enabled}
        aria-label={label}
        data-testid={`${testid}-toggle`}
      >
        <span
          className={`absolute top-0.5 h-6 w-6 rounded-full bg-white shadow-soft transition-transform ${
            enabled ? "translate-x-5" : "translate-x-0.5"
          }`}
        />
      </button>
    </div>
  );
}

export default function CookieBanner() {
  const { t } = useTranslation();
  // null = undecided / not yet hydrated
  const [decision, setDecision] = useState(undefined);
  const [prefsOpen, setPrefsOpen] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  // Hydrate on mount + auto-handle GPC users.
  useEffect(() => {
    const stored = readConsent();
    if (stored) {
      setDecision(stored.decision);
      setAnalytics(!!stored.categories?.analytics);
      setMarketing(!!stored.categories?.marketing);
      return;
    }
    if (isGPC()) {
      const payload = writeConsent("reject", { method: "GPC" });
      setDecision(payload.decision);
      return;
    }
    setDecision(null); // show banner
  }, []);

  // External "open preferences" event (footer link / page link)
  useEffect(() => {
    const onOpen = () => { setPrefsOpen(true); };
    window.addEventListener(OPEN_PREFS_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_PREFS_EVENT, onOpen);
  }, []);

  const acceptAll = () => {
    const p = writeConsent("all", { analytics: true, marketing: true });
    setDecision(p.decision); setAnalytics(true); setMarketing(true); setPrefsOpen(false);
  };
  const rejectAll = () => {
    const p = writeConsent("reject");
    setDecision(p.decision); setAnalytics(false); setMarketing(false); setPrefsOpen(false);
  };
  const saveCustom = () => {
    const p = writeConsent("custom", { analytics, marketing });
    setDecision(p.decision); setPrefsOpen(false);
  };

  // Don't render anything until we've hydrated.
  if (decision === undefined) return null;

  const bannerVisible = decision === null && !prefsOpen;

  return (
    <>
      {/* Bottom banner — first-visit only */}
      {bannerVisible && (
        <div
          className="fixed inset-x-0 bottom-0 z-[80] px-4 pb-4 sm:px-6 sm:pb-6 pointer-events-none"
          data-testid="cookie-banner"
        >
          <div className="max-w-5xl mx-auto pointer-events-auto bg-white/95 backdrop-blur-xl border border-ink-200 shadow-pop rounded-3xl p-5 sm:p-6 flex flex-col md:flex-row md:items-center gap-4 md:gap-6">
            <div className="flex items-start gap-3 flex-1">
              <div className="w-10 h-10 rounded-full bg-cobalt-50 flex items-center justify-center flex-shrink-0">
                <Cookie size={22} weight="fill" className="text-cobalt-500" />
              </div>
              <div>
                <div className="font-display font-semibold text-base">
                  {t("cookieBanner.title", "We use cookies to keep BlueRoam running smoothly.")}
                </div>
                <p className="text-sm text-ink-700 mt-1 leading-relaxed">
                  {t("cookieBanner.body", "Strictly necessary cookies are always on. Analytics and marketing cookies are off until you allow them. You can change your mind any time from the footer.")}{" "}
                  <Link to="/legal/cookies" className="font-semibold text-cobalt-600 hover:underline" data-testid="cookie-banner-policy-link">
                    {t("cookieBanner.learnMore", "Cookie Notice")}
                  </Link>
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 md:flex-shrink-0">
              <button
                type="button"
                onClick={() => setPrefsOpen(true)}
                className="btn-outline text-sm"
                data-testid="cookie-banner-prefs"
              >
                {t("cookieBanner.preferences", "Preferences")}
              </button>
              <button
                type="button"
                onClick={rejectAll}
                className="btn bg-ink-100 hover:bg-ink-200 text-ink-950 text-sm"
                data-testid="cookie-banner-reject"
              >
                {t("cookieBanner.reject", "Reject non-essential")}
              </button>
              <button
                type="button"
                onClick={acceptAll}
                className="btn-primary text-sm"
                data-testid="cookie-banner-accept"
              >
                {t("cookieBanner.accept", "Accept all")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Preferences modal */}
      {prefsOpen && (
        <div
          className="fixed inset-0 z-[90] bg-ink-950/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6"
          data-testid="cookie-prefs"
          onClick={(e) => { if (e.target === e.currentTarget) setPrefsOpen(false); }}
        >
          <div className="w-full sm:max-w-2xl bg-white rounded-t-3xl sm:rounded-3xl shadow-pop max-h-[92vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 sm:p-7 border-b border-ink-100">
              <div className="flex items-center gap-2.5">
                <Cookie size={22} weight="fill" className="text-cobalt-500" />
                <h3 className="font-display font-bold text-xl tracking-tight">
                  {t("cookieBanner.prefsTitle", "Cookie preferences")}
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setPrefsOpen(false)}
                aria-label="Close"
                className="w-9 h-9 rounded-full hover:bg-ink-100 flex items-center justify-center"
                data-testid="cookie-prefs-close"
              >
                <X size={18} weight="bold" />
              </button>
            </div>
            <div className="p-5 sm:p-7 space-y-1">
              <p className="text-sm text-ink-700 mb-4">
                {t("cookieBanner.prefsIntro", "Choose which categories of cookies BlueRoam can set on your device. You can change this any time from the website footer.")}
              </p>
              {isGPC() && (
                <div className="flex items-start gap-3 rounded-2xl bg-mint-soft/40 border border-mint-soft/80 p-4 mb-4 text-sm">
                  <ShieldCheck size={18} weight="bold" className="text-mint flex-shrink-0 mt-0.5" />
                  <div>
                    <strong>{t("cookieBanner.gpcTitle", "Global Privacy Control detected.")}</strong>{" "}
                    {t("cookieBanner.gpcBody", "We honour your GPC signal and have turned off analytics and marketing cookies. You can override that here if you want — it only affects this browser.")}
                  </div>
                </div>
              )}

              <CategoryRow
                testid="cookie-prefs-necessary"
                label={t("cookieBanner.catNec", "Strictly necessary")}
                desc={t("cookieBanner.catNecDesc", "Required for login, checkout, language preference and security. Cannot be disabled.")}
                enabled={true}
                locked={true}
                onChange={() => {}}
              />
              <CategoryRow
                testid="cookie-prefs-analytics"
                label={t("cookieBanner.catAna", "Analytics")}
                desc={t("cookieBanner.catAnaDesc", "Aggregate usage data so we can improve the site. IP addresses are truncated.")}
                enabled={analytics}
                locked={false}
                onChange={setAnalytics}
              />
              <CategoryRow
                testid="cookie-prefs-marketing"
                label={t("cookieBanner.catMkt", "Marketing")}
                desc={t("cookieBanner.catMktDesc", "Personalised messaging and cross-context advertising. Off by default. We do not sell personal information.")}
                enabled={marketing}
                locked={false}
                onChange={setMarketing}
              />
            </div>
            <div className="p-5 sm:p-7 border-t border-ink-100 flex flex-wrap gap-2 justify-end">
              <button
                type="button"
                onClick={rejectAll}
                className="btn bg-ink-100 hover:bg-ink-200 text-ink-950 text-sm"
                data-testid="cookie-prefs-reject"
              >
                {t("cookieBanner.reject", "Reject non-essential")}
              </button>
              <button
                type="button"
                onClick={saveCustom}
                className="btn-outline text-sm"
                data-testid="cookie-prefs-save"
              >
                <Check size={14} weight="bold" />
                {t("cookieBanner.save", "Save preferences")}
              </button>
              <button
                type="button"
                onClick={acceptAll}
                className="btn-primary text-sm"
                data-testid="cookie-prefs-accept"
              >
                {t("cookieBanner.accept", "Accept all")}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
